import { Flex, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import { IconChip, Toggle } from "@libertify/ui";
import { getConfigItemVisual, type ConfigItemVisualId } from "../data/addons";

interface PlaybookAddOnTitleProps {
  itemId: ConfigItemVisualId;
  title: string;
  /** Estimated credit cost. Intentionally not displayed until the backend
   *  exposes real values; callers keep passing it so the badge is easy to
   *  restore. */
  credits?: number;
  description: string;
  /**
   * Whether the add-on is shown to viewers of the experience. The header only
   * renders the visibility toggle when `onVisibleChange` is supplied (the
   * create flow has nothing to toggle yet — an add-on being in the draft *is*
   * its visibility there).
   */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  /** A request for this add-on is in flight: the toggle shows a spinner and
   *  can't be flipped again. */
  visiblePending?: boolean;
}

export const PlaybookAddOnTitle: React.FC<PlaybookAddOnTitleProps> = ({
  itemId,
  title,
  description,
  visible,
  onVisibleChange,
  visiblePending,
}) => {
  const t = useTranslate();
  const visibleLabel = t("createExperience.addOns.visible");
  const { Icon, color, weight } = getConfigItemVisual(itemId);
  return (
    <Flex align="flex-start" justify="flex-start" gap={16} wrap="wrap">
      <IconChip color={color} size="l" icon={<Icon weight={weight} />} />
      <Flex vertical gap={4} style={{ minWidth: 0, flex: 1 }}>
        <Flex align="center" gap={8}>
          <Typography.Text style={{ fontSize: 20, lineHeight: 1 }}>
            {title}
          </Typography.Text>
        </Flex>
        <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
          {description}
        </Typography.Text>
      </Flex>
      {onVisibleChange ? (
        <Flex align="center" gap={8}>
          <Typography.Text type="secondary" style={{ fontWeight: 500 }}>
            {visibleLabel}
          </Typography.Text>
          <Toggle
            size="l"
            checked={visible}
            onChange={onVisibleChange}
            loading={visiblePending}
            disabled={visiblePending}
            aria-label={visibleLabel}
          />
        </Flex>
      ) : null}
    </Flex>
  );
};
